import React, {useState, useEffect, useMemo} from "react";
import {
    Box, Container, Paper, Grid, Tab, Tabs, Chip, Stack, Tooltip,
    FormControl, InputLabel, Select, MenuItem, Button, useMediaQuery
} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import MainLayout from "../MainLoyout/MainLayout";
import Title from "../Title";
import Api from "../../Services/Api";
import {DataGrid, GridToolbar} from "@mui/x-data-grid";
import Skeleton from "@mui/material/Skeleton";
import {DesktopDatePicker, MobileDatePicker} from "@mui/x-date-pickers";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

const formatNumber = (value) => {
    if (isNaN(value) || value === null) return "0";
    return parseFloat(value).toLocaleString(undefined, {maximumFractionDigits: 2});
};

const statusProps = (status) => {
    if (status === "critical") return {label: "STOP", color: "error", variant: "filled"};
    if (status === "warning") return {label: "REVIEW", color: "warning", variant: "filled"};
    return {label: "OK", color: "success", variant: "outlined"};
};

const rpmColor = (rpm) => {
    if (rpm >= 2.5) return "success";
    if (rpm >= 1.8) return "warning";
    return "error";
};

const getColumns = () => [
    {
        field: "status",
        headerName: "Status",
        width: 100,
        renderCell: (params) => {
            const props = statusProps(params.value);
            return (
                <Tooltip title={params.row.recommendation} arrow placement="right">
                    <Chip label={props.label} size="small" color={props.color} variant={props.variant} sx={{fontSize: "0.7rem", fontWeight: 700}}/>
                </Tooltip>
            );
        },
    },
    {
        field: "site",
        headerName: "Website",
        flex: 1,
        minWidth: 150,
    },
    {
        field: "category",
        headerName: "Category",
        width: 150,
    },
    {
        field: "rpm",
        headerName: "RPM",
        description: "Revenue per 1K monetized searches (from demand_feed_stats)",
        type: "number",
        width: 90,
        renderCell: (params) => (
            <Tooltip title={`$${formatNumber(params.row.revenue)} / ${formatNumber(params.row.searches)} searches × 1000 = $${params.value}`} arrow>
                <Chip label={"$" + params.value} size="small" color={rpmColor(params.value)} variant="outlined"/>
            </Tooltip>
        ),
    },
    {
        field: "visits_per_day",
        headerName: "Visits/day",
        type: "number",
        width: 100,
        renderCell: (params) => (
            <Tooltip title={`${formatNumber(params.row.finally_started)} total visitors / ${params.row.days} days`} arrow>
                <span>{formatNumber(params.value)}</span>
            </Tooltip>
        ),
    },
    {
        field: "rpm_visit",
        headerName: "RPM/Visit",
        type: "number",
        width: 100,
        renderCell: (params) => (
            <Tooltip title={`$${formatNumber(params.row.revenue)} / ${formatNumber(params.row.finally_started)} visitors × 1000. True revenue per 1K visitors.`} arrow>
                <Chip label={"$" + params.value} size="small" color={rpmColor(params.value)} variant="outlined"/>
            </Tooltip>
        ),
    },
    {
        field: "ctr_visits",
        headerName: "CTR/Visits",
        description: "clicks / finally_started — real CTR over actual visitors",
        type: "number",
        width: 100,
        renderCell: (params) => (
            <Tooltip title={`${formatNumber(params.row.clicks)} clicks / ${formatNumber(params.row.finally_started)} visitors = ${params.value}%. This is the CTR the partner sees.`} arrow>
                <Chip
                    label={params.value + "%"}
                    size="small"
                    color={params.value >= 9 ? "error" : params.value >= 7 ? "warning" : "success"}
                    variant={params.value >= 9 ? "filled" : "outlined"}
                />
            </Tooltip>
        ),
    },
    {
        field: "rev_per_day",
        headerName: "$/day",
        type: "number",
        width: 90,
        valueFormatter: (params) => "$" + formatNumber(params.value),
    },
    {
        field: "revenue",
        headerName: "Revenue",
        type: "number",
        width: 100,
        valueFormatter: (params) => "$" + formatNumber(params.value),
    },
    // Hidden by default — user can enable via column visibility toolbar
    {
        field: "account",
        headerName: "Account",
        width: 130,
        valueFormatter: (params) => params.value?.replace("Taboola ", ""),
    },
    {
        field: "finally_started",
        headerName: "Visitors",
        description: "finally_started from tiger_stats",
        type: "number",
        width: 100,
        valueFormatter: (params) => formatNumber(params.value),
    },
    {
        field: "searches",
        headerName: "Monetized",
        description: "searches from demand_feed_stats",
        type: "number",
        width: 100,
        valueFormatter: (params) => formatNumber(params.value),
    },
    {
        field: "coverage",
        headerName: "Coverage",
        type: "number",
        width: 90,
        renderCell: (params) => (
            <Tooltip title={`${formatNumber(params.row.searches)} monetized / ${formatNumber(params.row.finally_started)} visitors = ${params.value}%`} arrow>
                <span>{params.value}%</span>
            </Tooltip>
        ),
    },
    {
        field: "clicks",
        headerName: "Clicks",
        type: "number",
        width: 90,
        valueFormatter: (params) => formatNumber(params.value),
    },
    {
        field: "ctr",
        headerName: "CTR",
        type: "number",
        width: 70,
        renderCell: (params) => (
            <Tooltip title={`${formatNumber(params.row.clicks)} clicks / ${formatNumber(params.row.searches)} monetized`} arrow>
                <span>{params.value}%</span>
            </Tooltip>
        ),
    },
    {
        field: "recommendation",
        headerName: "Recommendation",
        flex: 1,
        minWidth: 200,
        renderCell: (params) => (
            <span style={{
                fontSize: "0.78rem",
                color: params.row.status === "critical" ? "#d32f2f" : params.row.status === "warning" ? "#ed6c02" : "#2e7d32",
            }}>{params.value}</span>
        ),
    },
];

const defaultHiddenColumns = {
    account: false,
    finally_started: false,
    searches: false,
    clicks: false,
    ctr: false,
    recommendation: false,
};

const LoadingSkeleton = () => (
    <Box sx={{height: "max-content"}}>
        {[...Array(15)].map((_, key) => (
            <Skeleton key={key} variant="rectangular" sx={{my: 2, mx: 1}}/>
        ))}
    </Box>
);

const FeedPerformance = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const [summary, setSummary] = useState(null);
    const [startDate, setStartDate] = useState(dayjs().subtract(6, "day"));
    const [endDate, setEndDate] = useState(dayjs());
    const [activeTab, setActiveTab] = useState(0);
    const [pageSize, setPageSize] = useState(100);

    const columns = useMemo(() => getColumns(), []);

    const fetchData = async (from, to) => {
        setLoading(true);
        const result = await Api.feedPerformance({
            from: from.format("YYYY-MM-DD"),
            to: to.format("YYYY-MM-DD"),
        });
        if (result && result.success) {
            setData(result.data.map((row, idx) => ({...row, id: idx})));
            setSummary(result.summary);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchData(startDate, endDate);
    }, []);

    const handlePreset = (days) => {
        const to = dayjs();
        const from = dayjs().subtract(days - 1, "day");
        setStartDate(from);
        setEndDate(to);
        fetchData(from, to);
    };

    const handleDateChange = (type, value) => {
        if (type === "start") {
            setStartDate(value);
            fetchData(value, endDate);
        } else {
            setEndDate(value);
            fetchData(startDate, value);
        }
    };

    const accounts = useMemo(() => {
        if (!summary?.accounts) return [];
        return summary.accounts;
    }, [summary]);

    const filteredData = useMemo(() => {
        if (activeTab === 0) return data;
        const account = accounts[activeTab - 1];
        return data.filter((row) => row.account_id === account.id);
    }, [data, activeTab, accounts]);

    const tabSummary = useMemo(() => {
        const rows = filteredData;
        const totalFS = rows.reduce((s, r) => s + r.finally_started, 0);
        const totalS = rows.reduce((s, r) => s + r.searches, 0);
        const totalC = rows.reduce((s, r) => s + r.clicks, 0);
        const totalR = rows.reduce((s, r) => s + r.revenue, 0);
        const days = rows.length > 0 ? rows[0].days || 7 : 7;
        return {
            sites: rows.length,
            revenue: "$" + formatNumber(totalR),
            rev_per_day: "$" + formatNumber(totalR / days),
            rpm_visit: totalFS > 0 ? "$" + (totalR / totalFS * 1000).toFixed(2) : "$0",
        };
    }, [filteredData]);

    const DatePickerComponent = isMobile ? MobileDatePicker : DesktopDatePicker;

    return (
        <MainLayout>
            <Container maxWidth="xl" sx={{mt: isMobile ? 2 : 4, mb: 4, px: isMobile ? 1 : 3}}>
                <Paper sx={{p: isMobile ? 1 : 2}}>
                    {/* Header */}
                    <Box sx={{
                        display: "flex",
                        flexDirection: isMobile ? "column" : "row",
                        justifyContent: "space-between",
                        alignItems: isMobile ? "stretch" : "center",
                        gap: 1,
                        mb: 2
                    }}>
                        <Title>Feed Performance</Title>
                        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" sx={{gap: 0.5}}>
                            <Button size="small" variant="outlined" onClick={() => handlePreset(7)} sx={{minWidth: 36}}>7d</Button>
                            <Button size="small" variant="outlined" onClick={() => handlePreset(14)} sx={{minWidth: 36}}>14d</Button>
                            <Button size="small" variant="outlined" onClick={() => handlePreset(30)} sx={{minWidth: 40}}>30d</Button>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePickerComponent
                                    label="From"
                                    value={startDate}
                                    onChange={(d) => handleDateChange("start", d)}
                                    slotProps={{textField: {size: "small", sx: {width: isMobile ? 120 : 140}}}}
                                />
                                <DatePickerComponent
                                    label="To"
                                    value={endDate}
                                    onChange={(d) => handleDateChange("end", d)}
                                    slotProps={{textField: {size: "small", sx: {width: isMobile ? 120 : 140}}}}
                                />
                            </LocalizationProvider>
                        </Stack>
                    </Box>

                    {/* Summary */}
                    {!loading && (
                        <Stack direction="row" spacing={1} sx={{mb: 2, flexWrap: "wrap", gap: 0.5}}>
                            <Chip label={`${tabSummary.sites} sites`} size="small"/>
                            <Chip label={`Rev: ${tabSummary.revenue}`} size="small" color="success" variant="outlined"/>
                            <Chip label={`${tabSummary.rev_per_day}/day`} size="small" variant="outlined"/>
                            <Chip label={`RPM/V: ${tabSummary.rpm_visit}`} size="small" color="primary" variant="outlined"/>
                        </Stack>
                    )}

                    {/* Account Tabs */}
                    {!loading && (
                        <Tabs
                            value={activeTab}
                            onChange={(e, v) => setActiveTab(v)}
                            variant="scrollable"
                            scrollButtons="auto"
                            sx={{mb: 1}}
                        >
                            <Tab label={`All (${data.length})`}/>
                            {accounts.map((a) => {
                                const count = data.filter((r) => r.account_id === a.id).length;
                                return <Tab key={a.id} label={`${a.name.replace("Taboola ", "")} (${count})`}/>;
                            })}
                        </Tabs>
                    )}

                    {/* DataGrid */}
                    <DataGrid
                        rows={!loading ? filteredData : []}
                        columns={columns}
                        pageSize={pageSize}
                        onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                        autoHeight={true}
                        disableSelectionOnClick
                        initialState={{
                            sorting: {sortModel: [{field: "rpm_visit", sort: "desc"}]},
                            columns: {columnVisibilityModel: defaultHiddenColumns},
                        }}
                        components={{
                            LoadingOverlay: LoadingSkeleton,
                            Toolbar: GridToolbar,
                        }}
                        componentsProps={{
                            toolbar: {
                                showQuickFilter: true,
                                quickFilterProps: {debounceMs: 500},
                            },
                        }}
                        sx={{
                            mt: 1,
                            mb: 2,
                            minHeight: 600,
                        }}
                        density="compact"
                        loading={loading}
                    />
                </Paper>
            </Container>
        </MainLayout>
    );
};

export default FeedPerformance;
